import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import { useToast } from '../components/shared/Toast'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import { generateDoctorSummaryPDF } from '../components/reports/generateDoctorSummaryPDF'
import EmailModal from '../components/shared/EmailModal'

const BEHAVIOURS = [
  ['behaviour_agitation', 'Agitation'], ['behaviour_wandering', 'Wandering'], ['behaviour_confusion', 'Confusion'],
  ['behaviour_sundowning', 'Sundowning'], ['behaviour_sleep_disturbance', 'Sleep disturbance'],
  ['behaviour_appetite_change', 'Appetite change'], ['behaviour_fall', 'Fall'],
]
const fmtShort = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
const dISO = (d) => d.toLocaleDateString('en-CA')

function weekStart(iso) {
  const d = new Date(iso + 'T00:00:00')
  const diff = (d.getDay() + 6) % 7 // days since Monday
  d.setDate(d.getDate() - diff)
  return d
}

export default function ReportsPage() {
  const { recipient } = useCare()
  const [emailing, setEmailing] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)

  const build = useCallback(async () => {
    if (!recipient) return
    const since = new Date(); since.setDate(since.getDate() - 30)
    const sinceISO = dISO(since)
    const today = dISO(new Date())

    const [medRes, medLogRes, careLogRes, apptRes] = await Promise.all([
      supabase.from('medications').select('name, dose, timing, purpose').eq('care_recipient_id', recipient.id).eq('is_active', true).order('name'),
      supabase.from('medication_logs').select('status, medication:medications(name)').eq('care_recipient_id', recipient.id).gte('log_date', sinceISO),
      supabase.from('care_logs').select('*').eq('care_recipient_id', recipient.id).gte('log_date', sinceISO),
      supabase.from('appointments').select('doctor_name, appointment_date, purpose, action_items').eq('care_recipient_id', recipient.id).gte('appointment_date', sinceISO).lte('appointment_date', today).order('appointment_date', { ascending: false }),
    ])

    // Adherence per medication
    const adh = {}
    for (const l of medLogRes.data || []) {
      const name = l.medication?.name || 'Unknown'
      adh[name] = adh[name] || { name, taken: 0, missed: 0, refused: 0 }
      if (adh[name][l.status] != null) adh[name][l.status]++
    }

    // Mood weekly + behaviours
    const weeks = {}
    const behaviourCounts = {}
    for (const log of careLogRes.data || []) {
      const ws = dISO(weekStart(log.log_date))
      weeks[ws] = weeks[ws] || { moods: [] }
      for (const m of [log.mood_morning, log.mood_afternoon, log.mood_evening]) if (m != null) weeks[ws].moods.push(m)
      for (const [k] of BEHAVIOURS) if (log[k]) behaviourCounts[k] = (behaviourCounts[k] || 0) + 1
    }
    const moodWeekly = Object.keys(weeks).sort().map((ws) => {
      const wsD = new Date(ws + 'T00:00:00'); const we = new Date(wsD); we.setDate(we.getDate() + 6)
      const moods = weeks[ws].moods
      return { week: `${fmtShort(wsD)}–${fmtShort(we)}`, avg: moods.length ? Math.round((moods.reduce((a, b) => a + b, 0) / moods.length) * 10) / 10 : 0, n: moods.length }
    }).filter((w) => w.n > 0)

    const behaviourList = BEHAVIOURS.filter(([k]) => behaviourCounts[k]).map(([k, label]) => ({ label, count: behaviourCounts[k] }))
    const flaggedConcerns = behaviourList.filter((b) => b.count >= 3)

    setSummary({
      generatedAt: new Date().toLocaleDateString(),
      medications: medRes.data || [],
      adherence: Object.values(adh),
      moodWeekly,
      behaviourCounts: behaviourList,
      flaggedConcerns,
      appointments: apptRes.data || [],
      counts: { careLogs: (careLogRes.data || []).length, medLogs: (medLogRes.data || []).length, appts: (apptRes.data || []).length },
    })
    setLoading(false)
  }, [recipient])

  useEffect(() => { build() }, [build])

  if (!recipient || loading) return <LoadingSpinner label="Building summary…" />

  const s = summary
  return (
    <div className="stack">
      <button onClick={() => navigate('/more')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}>
        ← More</button>

      <div>
        <h1 className="title">Doctor summary</h1>
        <p className="subtitle mt-4">Last 30 days for {recipient.preferred_name || recipient.full_name}</p>
      </div>

      <div className="card card-tint stack" style={{ gap: 6 }}>
        <div className="row" style={{ gap: 8 }}><span style={{ fontSize: 22 }}>🩺</span><div style={{ fontWeight: 700 }}>Ready to print for the GP</div></div>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>A clean one-tap summary of medications, adherence, mood, behaviours, and visits.</p>
      </div>

      {/* Preview */}
      <div className="card stack" style={{ gap: 12 }}>
        <Stat label="Care logs" value={`${s.counts.careLogs} in 30 days`} />
        <Stat label="Medication logs" value={`${s.counts.medLogs} recorded`} />
        <Stat label="Appointments" value={`${s.counts.appts} in period`} />
        <Stat label="Active medications" value={`${s.medications.length}`} />
        {s.flaggedConcerns.length > 0 && (
          <div className="card" style={{ background: 'var(--amber-light)', borderColor: 'transparent', padding: 12 }}>
            <div style={{ fontWeight: 700, color: 'var(--amber)', marginBottom: 4 }}>Flagged concerns</div>
            {s.flaggedConcerns.map((c) => <div key={c.label} style={{ fontSize: 14 }}>{c.label} — {c.count}×</div>)}
          </div>
        )}
        {s.moodWeekly.length > 0 && (
          <div>
            <div className="section-title">Weekly mood</div>
            <div className="row wrap mt-4" style={{ gap: 8 }}>
              {s.moodWeekly.map((w) => <span key={w.week} className="badge">{w.week}: {w.avg}/5</span>)}
            </div>
          </div>
        )}
      </div>

      <div className="row no-print" style={{ gap: 8 }}>
        <button
          className="btn btn-lg grow"
          onClick={() => { try { generateDoctorSummaryPDF(recipient, s) } catch (e) { toast.error('Could not create the PDF.') } }}
        >
          Print PDF
        </button>
        <button className="btn btn-lg btn-secondary grow" onClick={() => setEmailing(true)}>
          Email
        </button>
      </div>

      <div style={{ height: 8 }} />

      {emailing && (
        <EmailModal
          title="Email the 30-day summary"
          description="Sends the same content as the PDF, formatted for reading on a phone."
          onClose={() => setEmailing(false)}
          buildPayload={() => ({
            kind: 'doctor_summary',
            person_name: recipient?.preferred_name || recipient?.full_name,
            care_stage: recipient?.care_stage,
            summary: `30-day summary covering ${s.rangeLabel || 'the last 30 days'}.`,
            goals: [],
            risk_flags: (s.flaggedConcerns || []).map((c) => ({
              severity: c.count >= 3 ? 'high' : 'medium',
              flag: c.label,
              reason: `Recorded ${c.count} time${c.count === 1 ? '' : 's'} in the last 30 days.`,
            })),
            daily_actions: [],
            what_to_track: (s.moodWeekly || []).map((w) => `Week of ${w.week}: average mood ${w.avg}/5`),
          })}
        />
      )}
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div className="spread">
      <span className="label">{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  )
}
